import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  MenuFoldOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { Button, Col, Layout, Menu, Row, Tooltip } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import React, { useState } from 'react';
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'src/constants/routes';
import useUserLogged from 'src/hooks/useUserLogged';
import { getAuth } from 'src/redux/slices/auth';
import ErrorScreen from 'src/screens/error';
import ErrorModal from 'src/screens/error/modals/ErrorModal';
import AvatarEmpty from 'src/screens/shared/components/AvatarEmpty';
import { AvatarCustom } from 'src/screens/shared/Styles';

import BetaModal from '../BetaModal';
import AccountSelector from './AccountSelector';
import {
  AvatarContainer,
  AvatarText,
  ButtonCollapsed,
  FlexContainer,
  FooterSidebarMobile,
  Header,
  HeaderLeft,
  LogoSidebarInv360,
  Logout,
  MenuMobile,
  MobileSidebar,
  PageTitle,
  SiderDesktop,
  UserControls,
} from './styles';
import useLinks from './useLinks';

type Props = {
  pageTitle: string;
  user: User;
  logout: any;
  children: any;
};

const Index = ({ user, children, logout, pageTitle }: Props) => {
  const { t } = useTranslation();
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const navigate = useNavigate();
  const auth = useSelector(getAuth);
  const { isSuperAdminOnlyLogged } = useUserLogged();
  const [collapsed, setCollapsed] = useState(true);
  const { links } = useLinks({setCollapsed, setMobileSidebar});
  const [isBetaModalVisible, setIsBetaModalVisible] = useState(true);

  const onLogout = () => {
    logout();
  };

  const showMobileSidebar = () => {
    setMobileSidebar(true);
  };

  const onCloseMobileSidebar = () => {
    setMobileSidebar(false);
  };

  const handleAccountSubmit = () => {
    setCollapsed(true);
    navigate(ROUTES.INVENTORY);
  };

  return (
    <>
      <Layout>
        {isBetaModalVisible && <BetaModal onClose={() => {setIsBetaModalVisible(false)}}/> }
        <SiderDesktop
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          theme="dark"
          collapsedWidth={60}
          width={200}
          trigger={null}
          style={{ overflow: 'hidden', height: '100vh', position: 'sticky', top: 0, left: 0 }}
        >
          <LogoSidebarInv360 className={collapsed ? 'collapsed' : '' }>
            <img className="closed-logo" src="/images/logo-sider.svg" alt="logo" />
            <img className="expanded-logo" src="/images/logo-sider-expand.svg" alt="logo" />
            <span className='beta'>Beta</span>
          </LogoSidebarInv360>
          <AccountSelector isCollapsed={collapsed} onSubmit={handleAccountSubmit} />
          <Menu
            data-testid="desktop-menu"
            activeKey={window.location.pathname.split('?')[0]}
            theme="dark"
            mode="inline"
            items={links}
          />
          <ButtonCollapsed
            data-testid="collapse-button"
            onClick={() => setCollapsed(!collapsed)}
            className={collapsed ? 'close' : 'open'}
          >
            {collapsed ? (
              <ArrowRightOutlined />
            ) : (
              <>
                <ArrowLeftOutlined /> {t('layout:close')}
              </>
            )}
          </ButtonCollapsed>
        </SiderDesktop>
        <Layout>
          <Header>
            <HeaderLeft>
              <MenuMobile
                icon={<MenuOutlined />}
                onClick={showMobileSidebar}
                type="text"
              ></MenuMobile>
              <PageTitle>{pageTitle}</PageTitle>
            </HeaderLeft>

            <UserControls>
              <Row>
                <Col xs={0} sm={24}>
                  <FlexContainer>
                    { auth.superAdminUser && auth.currentUser && <AvatarContainer className='is-super-admin'>
                      <AvatarEmpty
                        char1={auth.superAdminUser.first_name.charAt(0)}
                        char2={auth.superAdminUser.last_name.charAt(0)}
                        highlighted
                      />
                      <span className='super_admin'>S</span>
                    </AvatarContainer>
                    }
                    <AvatarContainer>
                      {user.avatar && !isSuperAdminOnlyLogged ? (
                        <AvatarCustom src={user.avatar} />
                      ) : (
                        <AvatarEmpty char1={user.first_name.charAt(0)} char2={user.last_name.charAt(0)} />
                      )}
                      <AvatarText>
                        <p>{`${user.first_name} ${user.last_name}`}</p>
                        <span>{user.role.name}</span>
                      </AvatarText>
                    </AvatarContainer>
                    <Tooltip title={t('layout:logout')} placement="bottom">
                      <Button
                        data-testid="logout-button"
                        onClick={onLogout}
                        icon={<Logout />}
                        type="text"
                        shape="circle"
                      />
                    </Tooltip>
                  </FlexContainer>
                </Col>
              </Row>
            </UserControls>
          </Header>
          <ErrorBoundary fallbackRender={({ error }) => <ErrorScreen error={error} />}>
            <Content className="content-app-layout">{children}</Content>
          </ErrorBoundary>
        </Layout>
      </Layout>
      <MobileSidebar
        data-testid="mobile-menu"
        placement={'left'}
        closable={true}
        onClose={onCloseMobileSidebar}
        closeIcon={<MenuFoldOutlined />}
        open={mobileSidebar}
        width={300}
        className="menu-mobile-style"
        title={
          <LogoSidebarInv360>
            <img className="expanded-logo" src="/images/logo-sider-expand.svg" alt="logo" />
            <span className='beta'>Beta</span>
          </LogoSidebarInv360>
        }
        extra={
          <>
            <FooterSidebarMobile>
              <AvatarContainer>
                { auth.superAdminUser && auth.currentUser && <AvatarContainer className='is-super-admin'>
                  <AvatarEmpty
                    char1={auth.superAdminUser.first_name.charAt(0)}
                    char2={auth.superAdminUser.last_name.charAt(0)}
                    highlighted
                  />
                  <span className='super_admin'>S</span>
                </AvatarContainer>
                }
                {user.avatar && !isSuperAdminOnlyLogged ? (
                  <AvatarCustom src={user.avatar} />
                ) : (
                  <AvatarEmpty char1={user.first_name.charAt(0)} char2={user.last_name.charAt(0)} />
                )}
                <AvatarText>
                  <p>{`${user.first_name} ${user.last_name}`}</p>
                  <span>{user.role.name}</span>
                </AvatarText>
              </AvatarContainer>
              <Button
                icon={<Logout />}
                onClick={onLogout}
                type="text"
                shape="circle"
                data-testid="logout-button"
              ></Button>
            </FooterSidebarMobile>
          </>
        }
      >
        <AccountSelector isCollapsed={false} onSubmit={handleAccountSubmit} />
        <Menu
          mode="inline"
          items={links}
        />
      </MobileSidebar>

      <ErrorModal />
    </>
  );
};

export default Index;
